import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'
import { isElectron } from '@/utils/env'

export function LoginPage() {
  const navigate = useNavigate()
  const { refreshSession, login } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (!isElectron()) return
    if (!window.arkitectDesktop?.auth?.onBrowserSuccess || !window.arkitectDesktop?.auth?.onBrowserError) return

    const unsubscribeSuccess = window.arkitectDesktop.auth.onBrowserSuccess(async () => {
      try {
        await refreshSession()
        navigate('/ide', { replace: true })
      } catch (err) {
        setError('Failed to load session after browser login.')
        setSubmitting(false)
      }
    })
    const unsubscribeError = window.arkitectDesktop.auth.onBrowserError(payload => {
      setError(payload?.message || 'Browser sign-in failed. Please try again.')
      setSubmitting(false)
    })
    return () => {
      unsubscribeSuccess()
      unsubscribeError()
    }
  }, [navigate, refreshSession])

  const onBrowserLogin = async () => {
    setSubmitting(true)
    setError('')
    try {
      if (isElectron()) {
        if (window.arkitectDesktop?.auth?.openBrowserLogin) {
          await window.arkitectDesktop.auth.openBrowserLogin()
        } else {
          throw new Error('Browser login only available in Electron')
        }
      } else {
        // Browser fallback using mock credentials
        await login({ username, password })
        await refreshSession()
        navigate('/ide', { replace: true })
      }
    } catch (nextError) {
      setError(String(nextError?.message || 'Unable to open browser or login.'))
      setSubmitting(false)
    }
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div>
          <p className="eyebrow">Arkitect {isElectron() ? 'Desktop' : 'Web'} IDE</p>
          <h1>Sign in as a DevUser</h1>
          <p className="login-copy">
            {isElectron()
              ? 'Authenticate securely via your browser to start a desktop IDE session.'
              : 'Authenticate to start a web IDE session.'}
          </p>
        </div>

        {error ? <div className="form-error">{error}</div> : null}

        {!isElectron() && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}
            />
          </div>
        )}

        <button
          onClick={onBrowserLogin}
          disabled={submitting || (!isElectron() && (!username || !password))}
          style={{ padding: '12px', marginTop: '16px', width: '100%' }}
        >
          {submitting
            ? (isElectron() ? 'Waiting for browser...' : 'Signing in...')
            : (isElectron() ? 'Login with Browser' : 'Login')}
        </button>
      </div>
    </div>
  )
}
